/**
 * External Audit Section
 * Extracted from FinancePage.jsx
 */

import React from 'react';
import { FileText, AlertTriangle, Target, CheckCircle, Clock, XCircle, Info } from 'lucide-react';
import { externalAuditData } from '../../data/mockData';
import ExecutiveSummarySection from '../../components/ExecutiveSummarySection';

const ExternalAudit = ({ year, language }) => {
  const auditData = externalAuditData[year] || externalAuditData[2025];
  const prevAuditData = externalAuditData[year - 1] || externalAuditData[2024];
  
  const getStatusColor = (status) => {
    switch(status) {
      case 'strong': return 'border-emerald-200 bg-emerald-50/30';
      case 'inline': return 'border-slate-200';
      case 'attention': return 'border-amber-200 bg-amber-50/30';
      case 'pressure': return 'border-red-200 bg-red-50/30';
      default: return 'border-slate-200';
    }
  };

  const getStatusDot = (status) => {
    switch(status) {
      case 'strong': return 'bg-emerald-500';
      case 'inline': return 'bg-blue-500';
      case 'attention': return 'bg-amber-500';
      case 'pressure': return 'bg-red-500';
      default: return 'bg-slate-400';
    }
  };

  const getThemeStatusIcon = (status) => {
    switch(status) {
      case 'closed': return <CheckCircle className="w-4 h-4 text-emerald-500" />;
      case 'partial': return <Clock className="w-4 h-4 text-amber-500" />;
      case 'open': return <XCircle className="w-4 h-4 text-red-500" />;
      default: return null;
    }
  };

  const auditInsights = {
    pt: [
      { title: 'OPINIÃO 2025', description: '2025 fechou com opinião com reservas, mas circunscrita a matérias específicas e não a uma falha generalizada das demonstrações financeiras.', color: 'text-gold-400' },
      { title: 'NATUREZA DOS TEMAS', description: 'Os temas materiais concentram-se em formalização histórica, existências / saldos iniciais e provisão de reabilitação ambiental, todos com impacto potencial na fiabilidade e especialização dos saldos.', color: 'text-white' },
      { title: 'FOCO 2026', description: 'Em 2026, a prioridade deve ser fechar tecnicamente os temas legacy ainda abertos e preservar a melhoria já alcançada em confirmações externas e fecho histórico.', color: 'text-emerald-400' }
    ],
    en: [
      { title: '2025 OPINION', description: '2025 closed with a qualified opinion, but limited to specific matters and not a generalized failure of the financial statements.', color: 'text-gold-400' },
      { title: 'THEME NATURE', description: 'Material themes focus on historical formalization, inventories / opening balances and environmental rehabilitation provision, all with potential impact on reliability and balance specialization.', color: 'text-white' },
      { title: '2026 FOCUS', description: 'In 2026, the priority should be to technically close legacy themes still open and preserve improvements already achieved in external confirmations and historical closing.', color: 'text-emerald-400' }
    ]
  };

  return (
    <div className="space-y-6" data-testid="external-audit-section">
      {/* Page Title */}
      <div>
        <h2 className="font-serif text-3xl font-bold text-primary">
          {language === 'pt' ? 'Auditoria Externa' : 'External Audit'}
        </h2>
        <p className="text-slate-500 mt-2">
          {language === 'pt' 
            ? 'Opinião emitida, temas materiais de auditoria e prontidão de fecho 2025'
            : 'Issued opinion, material audit themes and 2025 closing readiness'}
        </p>
      </div>

      {/* Key Insights */}
      <div className="bg-gradient-to-r from-[#0A192F] to-[#1a2744] rounded-2xl p-6">
        <div className="flex items-center gap-2 mb-5">
          <div className="w-1 h-5 bg-gold-500 rounded-full"></div>
          <h3 className="text-gold-400 font-semibold text-sm uppercase tracking-wider">
            {language === 'pt' ? 'Mensagens-Chave' : 'Key Insights'}
          </h3>
        </div>
        <div className="grid grid-cols-3 gap-5">
          {auditInsights[language].map((insight, idx) => (
            <div 
              key={idx} 
              className="bg-slate-700/30 border border-slate-600/30 rounded-xl p-4 transition-all duration-300 hover:scale-[1.02]"
            >
              <div className="flex items-start gap-3">
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${
                  idx === 0 ? 'bg-gold-500/30' : idx === 1 ? 'bg-slate-500/30' : 'bg-emerald-500/30'
                }`}>
                  {idx === 0 ? (
                    <FileText className="w-5 h-5 text-gold-400" />
                  ) : idx === 1 ? (
                    <AlertTriangle className="w-5 h-5 text-slate-300" />
                  ) : (
                    <Target className="w-5 h-5 text-emerald-400" />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <h4 className={`font-semibold text-sm mb-1.5 leading-tight ${insight.color}`}>{insight.title}</h4>
                  <p className="text-slate-400 text-xs leading-relaxed">{insight.description}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-5 gap-4">
        {/* Opinion */}
        <div className={`bg-white rounded-xl border p-5 ${getStatusColor(auditData.opinion.status)}`}>
          <div className="flex items-center justify-between mb-2">
            <p className="text-xs text-slate-500 uppercase tracking-wider">{language === 'pt' ? 'Opinião Auditor 2025' : 'Auditor Opinion 2025'}</p>
            <div className={`w-2 h-2 rounded-full ${getStatusDot(auditData.opinion.status)}`}></div>
          </div>
          <p className={`font-serif text-xl font-semibold ${auditData.opinion.status === 'strong' ? 'text-emerald-600' : auditData.opinion.status === 'attention' ? 'text-amber-600' : 'text-primary'}`}>
            {auditData.opinion.value[language]}
          </p>
          <p className="text-xs text-slate-500 mt-2">
            {language === 'pt' ? 'vs ' : 'vs '}{prevAuditData.opinion.value[language]}
          </p>
        </div>

        {/* Reservations */}
        <div className={`bg-white rounded-xl border p-5 ${getStatusColor(auditData.reservations.status)}`}>
          <div className="flex items-center justify-between mb-2">
            <p className="text-xs text-slate-500 uppercase tracking-wider">{language === 'pt' ? 'Reservas' : 'Reservations'}</p>
            <div className={`w-2 h-2 rounded-full ${getStatusDot(auditData.reservations.status)}`}></div>
          </div>
          <p className={`font-serif text-3xl font-semibold ${auditData.reservations.value === 0 ? 'text-emerald-600' : 'text-amber-600'}`}>
            {auditData.reservations.value}
          </p>
          <p className={`text-xs mt-2 ${auditData.reservations.value <= auditData.reservations.prevValue ? 'text-emerald-600' : 'text-red-500'}`}>
            {auditData.reservations.value <= auditData.reservations.prevValue ? '' : '+'}{auditData.reservations.value - auditData.reservations.prevValue} vs LY
          </p>
        </div>

        {/* Scope Limitations */}
        <div className={`bg-white rounded-xl border p-5 ${getStatusColor(auditData.scopeLimitations.status)}`}>
          <div className="flex items-center justify-between mb-2">
            <p className="text-xs text-slate-500 uppercase tracking-wider">{language === 'pt' ? 'Limitações Âmbito' : 'Scope Limitations'}</p>
            <div className={`w-2 h-2 rounded-full ${getStatusDot(auditData.scopeLimitations.status)}`}></div>
          </div>
          <p className={`font-serif text-3xl font-semibold ${auditData.scopeLimitations.value === 0 ? 'text-emerald-600' : 'text-amber-600'}`}>
            {auditData.scopeLimitations.value}
          </p>
          <p className={`text-xs mt-2 ${auditData.scopeLimitations.value <= auditData.scopeLimitations.prevValue ? 'text-emerald-600' : 'text-red-500'}`}>
            {auditData.scopeLimitations.value <= auditData.scopeLimitations.prevValue ? '' : '+'}{auditData.scopeLimitations.value - auditData.scopeLimitations.prevValue} vs LY
          </p>
        </div>

        {/* Reservation Base 2025 */}
        <div className="bg-white rounded-xl border p-5 border-amber-200 bg-amber-50/30">
          <div className="flex items-center justify-between mb-2">
            <p className="text-xs text-slate-500 uppercase tracking-wider">{language === 'pt' ? 'Base da Reserva 2025' : 'Reservation Base 2025'}</p>
            <div className="w-2 h-2 rounded-full bg-amber-500"></div>
          </div>
          <p className="font-serif text-3xl font-semibold text-amber-600">3</p>
          <p className="text-xs text-slate-500 mt-2 leading-tight">
            {language === 'pt' ? 'Formalização histórica, existências/saldos iniciais, provisão ambiental' : 'Historical formalization, inventories/opening balances, environmental provision'}
          </p>
        </div>

        {/* Themes Resolved */}
        <div className="bg-white rounded-xl border p-5 border-emerald-200 bg-emerald-50/30">
          <div className="flex items-center justify-between mb-2">
            <p className="text-xs text-slate-500 uppercase tracking-wider">{language === 'pt' ? 'Temas Resolvidos vs Ciclo Ant.' : 'Themes Resolved vs Prior Cycle'}</p>
            <div className="w-2 h-2 rounded-full bg-emerald-500"></div>
          </div>
          <p className="font-serif text-3xl font-semibold text-emerald-600">2</p>
          <p className="text-xs text-slate-500 mt-2 leading-tight">
            {language === 'pt' ? 'Confirmações externas/reconciliações; aprovação 2021–2022' : 'External confirmations/reconciliations; 2021-2022 approval'}
          </p>
        </div>
      </div>

      {/* Executive Summary */}
      <ExecutiveSummarySection
        title={language === 'pt' ? 'Sumário Executivo' : 'Executive Summary'}
        subtitle={language === 'pt' ? 'Auditoria externa e conformidade' : 'External audit and compliance'}
        language={language}
        maxHeight="400px"
        icon={Info}
        dataTestId="external-audit-executive-summary"
      >
        <div className="space-y-4">
          {[
            {
              pt: 'A auditoria externa é um processo independente que valida a fiabilidade das demonstrações financeiras, sendo a opinião do auditor um indicador crítico da qualidade da informação financeira reportada aos sócios.',
              en: 'External audit is an independent process that validates the reliability of financial statements, with the auditor\'s opinion being a critical indicator of reported financial information quality to partners.'
            },
            {
              pt: 'Temas materiais em aberto representam áreas onde o auditor identificou limitações ou incertezas que podem afectar a opinião, requerendo acção de remediação pela Gerência.',
              en: 'Open material themes represent areas where the auditor identified limitations or uncertainties that may affect the opinion, requiring remediation action by Management.'
            },
            {
              pt: 'O progresso no encerramento de temas legacy e a melhoria contínua dos controlos internos são fundamentais para a transição para uma opinião sem reservas.',
              en: 'Progress in closing legacy themes and continuous improvement of internal controls are fundamental for transitioning to an unqualified opinion.'
            },
          ].map((item, idx) => (
            <div key={idx} className="flex items-start gap-4">
              <div className="flex-shrink-0 w-6 h-6 rounded-full bg-primary flex items-center justify-center mt-0.5">
                <span className="text-white text-xs font-bold">{idx + 1}</span>
              </div>
              <p className="text-slate-700 leading-relaxed text-sm">{item[language]}</p>
            </div>
          ))}
        </div>
      </ExecutiveSummarySection>

      {/* Critical Themes Matrix */}
      <div className="bg-white rounded-xl border border-slate-200 p-6">
        <h3 className="font-serif text-lg font-semibold text-primary mb-4">
          {language === 'pt' ? 'Temas Críticos por Categoria' : 'Critical Themes by Category'}
        </h3>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-slate-200">
                <th className="text-left py-2 px-3 text-xs font-semibold text-slate-500 uppercase">{language === 'pt' ? 'Categoria' : 'Category'}</th>
                <th className="text-center py-2 px-3 text-xs font-semibold text-slate-500 uppercase">{language === 'pt' ? 'Severidade' : 'Severity'}</th>
                <th className="text-center py-2 px-3 text-xs font-semibold text-slate-500 uppercase">2025</th>
                <th className="text-center py-2 px-3 text-xs font-semibold text-slate-500 uppercase">2024</th>
                <th className="text-center py-2 px-3 text-xs font-semibold text-slate-500 uppercase">2023</th>
                <th className="text-center py-2 px-3 text-xs font-semibold text-slate-500 uppercase">Status</th>
              </tr>
            </thead>
            <tbody>
              {auditData.criticalThemes.map((theme, idx) => (
                <tr key={idx} className="border-b border-slate-100 hover:bg-slate-50">
                  <td className="py-3 px-3 font-medium text-primary text-sm">{theme.category[language]}</td>
                  <td className="py-3 px-3 text-center">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                      theme.severity === 'high' ? 'bg-red-100 text-red-700' :
                      theme.severity === 'medium' ? 'bg-amber-100 text-amber-700' :
                      'bg-emerald-100 text-emerald-700'
                    }`}>
                      {theme.severity === 'high' ? (language === 'pt' ? 'Alta' : 'High') :
                       theme.severity === 'medium' ? (language === 'pt' ? 'Média' : 'Medium') :
                       (language === 'pt' ? 'Baixa' : 'Low')}
                    </span>
                  </td>
                  <td className="py-3 px-2 text-center font-semibold">{theme.findings[2025]}</td>
                  <td className="py-3 px-2 text-center text-slate-600">{theme.findings[2024]}</td>
                  <td className="py-3 px-2 text-center text-slate-500">{theme.findings[2023] ?? '-'}</td>
                  <td className="py-3 px-3 text-center">{getThemeStatusIcon(theme.status)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="mt-4 pt-3 border-t border-slate-100">
          <p className="text-xs text-slate-500 italic">
            {language === 'pt' 
              ? '2025 = estado de fecho pré-opinião | 2024 = temas da opinião emitida | 2023 = histórico'
              : '2025 = pre-opinion closing status | 2024 = themes from issued opinion | 2023 = historical'}
          </p>
        </div>
        <div className="flex justify-end gap-6 mt-3 text-xs text-slate-500">
          <div className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-emerald-500" /><span>{language === 'pt' ? 'Fechado' : 'Closed'}</span></div>
          <div className="flex items-center gap-2"><Clock className="w-4 h-4 text-amber-500" /><span>{language === 'pt' ? 'Parcial' : 'Partial'}</span></div>
          <div className="flex items-center gap-2"><XCircle className="w-4 h-4 text-red-500" /><span>{language === 'pt' ? 'Aberto' : 'Open'}</span></div>
        </div>
      </div>

      {/* Detailed Theme Explanations */}
      <div className="bg-white rounded-xl border border-slate-200 p-6">
        <h3 className="font-serif text-lg font-semibold text-primary mb-4">
          {language === 'pt' ? 'Detalhe dos Temas Materiais' : 'Material Themes Detail'}
        </h3>
        <div className="space-y-4">
          {/* Theme 1 */}
          <div className="border-l-4 border-red-400 pl-4 py-2">
            <div className="flex items-center gap-2 mb-1">
              <h4 className="font-semibold text-primary text-sm">{language === 'pt' ? 'Existências / Stockpiles e saldos iniciais' : 'Inventories / Stockpiles and opening balances'}</h4>
              <span className="px-1.5 py-0.5 bg-red-100 text-red-700 text-[10px] font-semibold rounded">{language === 'pt' ? 'Alta' : 'High'}</span>
              <span className="px-1.5 py-0.5 bg-amber-100 text-amber-700 text-[10px] font-semibold rounded">{language === 'pt' ? 'Parcial' : 'Partial'}</span>
            </div>
            <p className="text-xs text-slate-600 leading-relaxed">
              {language === 'pt' 
                ? 'Tema ainda sensível, dado que em 2024 o auditor refere limitação sobre o saldo inicial e, consequentemente, sobre a correcta especialização em 2024 de rubricas ligadas a consumos, variações de inventários e ganhos/perdas em existências.'
                : 'Still sensitive theme, as in 2024 the auditor noted limitation on opening balance and consequently on correct 2024 specialization of items related to consumption, inventory variations and inventory gains/losses.'}
            </p>
          </div>

          {/* Theme 2 */}
          <div className="border-l-4 border-red-400 pl-4 py-2">
            <div className="flex items-center gap-2 mb-1">
              <h4 className="font-semibold text-primary text-sm">{language === 'pt' ? 'Capitalização / Imobilizado / custos de desenvolvimento' : 'Capitalization / Fixed Assets / development costs'}</h4>
              <span className="px-1.5 py-0.5 bg-red-100 text-red-700 text-[10px] font-semibold rounded">{language === 'pt' ? 'Alta' : 'High'}</span>
              <span className="px-1.5 py-0.5 bg-amber-100 text-amber-700 text-[10px] font-semibold rounded">{language === 'pt' ? 'Parcial' : 'Partial'}</span>
            </div>
            <p className="text-xs text-slate-600 leading-relaxed">
              {language === 'pt' 
                ? 'Embora não apareça como base autónoma da opinião de 2024, deve entrar no radar 2025 como tema material de fecho.'
                : 'Although not appearing as standalone basis for 2024 opinion, should be on 2025 radar as material closing theme.'}
            </p>
          </div>

          {/* Theme 3 */}
          <div className="border-l-4 border-red-400 pl-4 py-2">
            <div className="flex items-center gap-2 mb-1">
              <h4 className="font-semibold text-primary text-sm">{language === 'pt' ? 'Provisão de reabilitação ambiental' : 'Environmental rehabilitation provision'}</h4>
              <span className="px-1.5 py-0.5 bg-red-100 text-red-700 text-[10px] font-semibold rounded">{language === 'pt' ? 'Alta' : 'High'}</span>
              <span className="px-1.5 py-0.5 bg-amber-100 text-amber-700 text-[10px] font-semibold rounded">{language === 'pt' ? 'Parcial' : 'Partial'}</span>
            </div>
            <p className="text-xs text-slate-600 leading-relaxed">
              {language === 'pt' 
                ? 'Em 2024, a empresa reconheceu uma provisão de 14.909.796 mKz, mas o auditor manteve limitação quanto à correcta especialização do reconhecimento entre exercícios.'
                : 'In 2024, the company recognized a provision of 14,909,796 mKz, but the auditor maintained limitation regarding correct specialization of recognition between periods.'}
            </p>
          </div>

          {/* Theme 4 - Open */}
          <div className="border-l-4 border-red-500 pl-4 py-2">
            <div className="flex items-center gap-2 mb-1">
              <h4 className="font-semibold text-primary text-sm">{language === 'pt' ? 'Formalização histórica de activos e passivos' : 'Historical formalization of assets and liabilities'}</h4>
              <span className="px-1.5 py-0.5 bg-red-100 text-red-700 text-[10px] font-semibold rounded">{language === 'pt' ? 'Alta' : 'High'}</span>
              <span className="px-1.5 py-0.5 bg-red-100 text-red-700 text-[10px] font-semibold rounded">{language === 'pt' ? 'Aberto' : 'Open'}</span>
            </div>
            <p className="text-xs text-slate-600 leading-relaxed">
              {language === 'pt' 
                ? 'Este é um dos temas mais críticos, porque o relatório indica que permanecia em curso a análise, quantificação e negociação para posterior formalização dos termos relativos à transferência dos activos e passivos.'
                : 'This is one of the most critical themes, as the report indicates that analysis, quantification and negotiation for subsequent formalization was still ongoing.'}
            </p>
          </div>

          {/* Theme 5 - Closed */}
          <div className="border-l-4 border-emerald-400 pl-4 py-2">
            <div className="flex items-center gap-2 mb-1">
              <h4 className="font-semibold text-primary text-sm">{language === 'pt' ? 'Confirmações externas e reconciliações' : 'External confirmations and reconciliations'}</h4>
              <span className="px-1.5 py-0.5 bg-amber-100 text-amber-700 text-[10px] font-semibold rounded">{language === 'pt' ? 'Média' : 'Medium'}</span>
              <span className="px-1.5 py-0.5 bg-emerald-100 text-emerald-700 text-[10px] font-semibold rounded">{language === 'pt' ? 'Fechado' : 'Closed'}</span>
            </div>
            <p className="text-xs text-slate-600 leading-relaxed">
              {language === 'pt' 
                ? 'O próprio relatório diz que a reserva do ciclo anterior relacionada com ausência de respostas de terceiros e reconciliações foi resolvida sem impacto material em 2024.'
                : 'The report itself states that the prior cycle reservation related to lack of third-party responses and reconciliations was resolved without material impact in 2024.'}
            </p>
          </div>

          {/* Theme 6 - Closed */}
          <div className="border-l-4 border-emerald-400 pl-4 py-2">
            <div className="flex items-center gap-2 mb-1">
              <h4 className="font-semibold text-primary text-sm">{language === 'pt' ? 'Fecho histórico / aprovação de exercícios anteriores' : 'Historical closing / approval of prior years'}</h4>
              <span className="px-1.5 py-0.5 bg-amber-100 text-amber-700 text-[10px] font-semibold rounded">{language === 'pt' ? 'Média' : 'Medium'}</span>
              <span className="px-1.5 py-0.5 bg-emerald-100 text-emerald-700 text-[10px] font-semibold rounded">{language === 'pt' ? 'Fechado' : 'Closed'}</span>
            </div>
            <p className="text-xs text-slate-600 leading-relaxed">
              {language === 'pt' 
                ? 'O relatório refere igualmente que a reserva anterior relativa à pendência de aprovação das demonstrações financeiras de 2021 e 2022 foi resolvida sem impacto material em 2024.'
                : 'The report also notes that the prior reservation related to pending approval of 2021 and 2022 financial statements was resolved without material impact in 2024.'}
            </p>
          </div>
        </div>
      </div>

      {/* ============ ARQUIVO DE RELATÓRIOS ============ */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="bg-gradient-to-r from-[#0A192F] to-[#1a2744] px-6 py-4">
          <h3 className="font-serif text-lg font-bold text-white">
            {language === 'pt' ? 'Arquivo de Relatórios do Auditor' : 'Auditor Reports Archive'}
          </h3>
          <p className="text-slate-400 text-sm mt-1">
            {language === 'pt' 
              ? 'Relatórios de auditoria independente por exercício fiscal'
              : 'Independent audit reports by fiscal year'}
          </p>
        </div>
        
        <div className="p-6">
          <div className="grid grid-cols-5 gap-4">
            {[2025, 2024, 2023, 2022, 2021].map((reportYear) => {
              const isCurrentYear = reportYear === 2025;
              const opinionType = reportYear === 2025 ? 'qualified' : reportYear >= 2023 ? 'qualified' : 'unqualified';
              const opinionLabel = {
                qualified: { pt: 'Com Reservas', en: 'Qualified' },
                unqualified: { pt: 'Sem Reservas', en: 'Unqualified' }
              };
              
              return (
                <div 
                  key={reportYear}
                  className={`rounded-xl border-2 overflow-hidden transition-all hover:shadow-lg cursor-pointer ${
                    isCurrentYear ? 'border-primary' : 'border-slate-200 hover:border-primary/50'
                  }`}
                >
                  {/* Year Header */}
                  <div className={`px-4 py-3 text-center ${isCurrentYear ? 'bg-primary' : 'bg-slate-100'}`}>
                    <span className={`font-serif text-2xl font-bold ${isCurrentYear ? 'text-white' : 'text-primary'}`}>
                      {reportYear}
                    </span>
                    {isCurrentYear && (
                      <span className="ml-2 px-2 py-0.5 bg-white/20 text-white text-[10px] font-semibold rounded">
                        {language === 'pt' ? 'ACTUAL' : 'CURRENT'}
                      </span>
                    )}
                  </div>
                  
                  {/* Report Details */}
                  <div className="p-4 space-y-3">
                    {/* Opinion Badge */}
                    <div className="text-center">
                      <span className={`inline-flex px-3 py-1 rounded-full text-xs font-semibold ${
                        opinionType === 'qualified' 
                          ? 'bg-amber-100 text-amber-700' 
                          : 'bg-emerald-100 text-emerald-700'
                      }`}>
                        {opinionLabel[opinionType][language]}
                      </span>
                    </div>
                    
                    {/* Documents */}
                    <div className="space-y-2">
                      <button className="w-full flex items-center justify-between p-2 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors text-left">
                        <div className="flex items-center gap-2">
                          <FileText className="w-4 h-4 text-primary" />
                          <span className="text-xs text-slate-700">
                            {language === 'pt' ? 'Relatório de Auditoria' : 'Audit Report'}
                          </span>
                        </div>
                        <svg className="w-4 h-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                        </svg>
                      </button>
                      
                      <button className="w-full flex items-center justify-between p-2 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors text-left">
                        <div className="flex items-center gap-2">
                          <FileText className="w-4 h-4 text-slate-400" />
                          <span className="text-xs text-slate-700">
                            {language === 'pt' ? 'Carta à Gestão' : 'Management Letter'}
                          </span>
                        </div>
                        <svg className="w-4 h-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                        </svg>
                      </button>
                    </div>
                    
                    {/* Audit Firm */}
                    <div className="pt-2 border-t border-slate-100 text-center">
                      <p className="text-[10px] text-slate-400 uppercase tracking-wider">
                        {language === 'pt' ? 'Auditor' : 'Auditor'}
                      </p>
                      <p className="text-xs font-medium text-slate-600">
                        {reportYear >= 2023 ? 'PwC Angola' : 'Deloitte Angola'}
                      </p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ExternalAudit;
